import API from "../../core/apiConfig";
import { normalizePaginatedResponse } from "../../core/response";
import type {
  DataLockApiEnvelope,
  DataLockCostConfigNode,
  DataLockFieldOption,
  DataLockFieldOptionParams,
  DataLockFieldOptionsApiEnvelope,
  DataLockListApiEnvelope,
  DataLockListParams,
  DataLockMutationPayload,
  DataLockOptions,
  DataLockPaginatedEnvelope,
  DataLockRecord,
} from "./types";

const isNonArrayObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isDataLockRecord = (value: unknown): value is DataLockRecord =>
  isNonArrayObject(value) &&
  typeof value._id === "string" &&
  value._id.length > 0 &&
  typeof value.name === "string" &&
  typeof value.feature === "string" &&
  typeof value.active === "boolean" &&
  Array.isArray(value.actions) &&
  isNonArrayObject(value.orderScope) &&
  isNonArrayObject(value.costSheetScope);

const isString = (value: unknown): value is string => typeof value === "string";

const isOptionFeature = (value: unknown): boolean =>
  isString(value) ||
  (isNonArrayObject(value) && isString(value.code) && isString(value.name));

const isOptionAction = (value: unknown): boolean =>
  isNonArrayObject(value) &&
  isString(value.code) &&
  isString(value.name) &&
  (value.permissionAction === undefined ||
    value.permissionAction === null ||
    isString(value.permissionAction));

const isOptionField = (value: unknown): boolean =>
  isNonArrayObject(value) &&
  isString(value.code) &&
  isString(value.name) &&
  (value.valueType === "REFERENCE" || value.valueType === "ENUM");

const isCodeName = (value: unknown): boolean =>
  isNonArrayObject(value) && isString(value.code) && isString(value.name);

const toTrimmedString = (value: unknown): string | undefined =>
  isString(value) && value.trim().length > 0 ? value.trim() : undefined;

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  Number.isInteger(value) &&
  value >= 0;

const isPositiveInteger = (value: unknown): value is number =>
  isNonNegativeInteger(value) && value >= 1;

const isDataLockOptions = (value: unknown): value is DataLockOptions => {
  if (!isNonArrayObject(value)) return false;

  const { features, actions, order, orderCostSheet } = value;
  return (
    Array.isArray(features) &&
    features.every(isOptionFeature) &&
    isNonArrayObject(actions) &&
    Object.values(actions).every(
      (actionList) => Array.isArray(actionList) && actionList.every(isOptionAction),
    ) &&
    isNonArrayObject(order) &&
    Array.isArray(order.fields) &&
    order.fields.every(isOptionField) &&
    isNonArrayObject(order.wildcard) &&
    isString(order.wildcard.value) &&
    isString(order.wildcard.label) &&
    isCodeName(order.dateField) &&
    isNonArrayObject(orderCostSheet) &&
    Array.isArray(orderCostSheet.groups) &&
    orderCostSheet.groups.every(isCodeName) &&
    isCodeName(orderCostSheet.dateField)
  );
};

const isCostConfigNode = (value: unknown): value is DataLockCostConfigNode => {
  if (
    !isNonArrayObject(value) ||
    !isString(value._id) ||
    value._id.length === 0 ||
    !isString(value.code) ||
    !isString(value.name)
  ) {
    return false;
  }

  if (value.active !== undefined && typeof value.active !== "boolean") return false;
  if (value.isLeaf !== undefined && typeof value.isLeaf !== "boolean") return false;
  if (
    value.groups !== undefined &&
    (!isNonArrayObject(value.groups) ||
      !Object.values(value.groups).every(
        (group) =>
          isNonArrayObject(group) &&
          (group.editable === undefined || typeof group.editable === "boolean"),
      ))
  ) {
    return false;
  }

  return value.children === undefined ||
    (Array.isArray(value.children) && value.children.every(isCostConfigNode));
};

const ensureSuccessfulMutation = async (
  request: Promise<{ data: DataLockApiEnvelope<unknown> }>,
): Promise<void> => {
  const response = (await request).data;
  if (response?.success !== true) {
    throw new Error(response?.message || "Không thể cập nhật khóa dữ liệu");
  }
};

const hasInvalidPaginationMetadata = (
  response: DataLockFieldOptionsApiEnvelope,
): boolean =>
  (response.totalItem !== undefined && !isNonNegativeInteger(response.totalItem)) ||
  (response.totalDocs !== undefined && !isNonNegativeInteger(response.totalDocs)) ||
  (response.limit !== undefined && !isPositiveInteger(response.limit)) ||
  (response.page !== undefined && !isPositiveInteger(response.page)) ||
  (response.totalPages !== undefined && !isPositiveInteger(response.totalPages));

export const getAllDataLocks = async (params: DataLockListParams) => {
  const response = await API.get<DataLockListApiEnvelope>(
    "/admin/data-locks",
    { params },
  ).then((res) => res.data);

  if (
    !response?.success ||
    !Array.isArray(response.data) ||
    !response.data.every(isDataLockRecord)
  ) {
    throw new Error(
      response?.message || "Không thể tải danh sách khóa dữ liệu",
    );
  }

  return normalizePaginatedResponse<DataLockRecord, "current">(
    response,
    "current",
  );
};

export const getDataLock = async (id: string): Promise<DataLockRecord> => {
  const response = await API.get<DataLockApiEnvelope<DataLockRecord>>(
    `/admin/data-locks/${id}`,
  ).then((res) => res.data);

  if (!response?.success || !isDataLockRecord(response.data)) {
    throw new Error(
      response?.message || "Không thể tải chi tiết khóa dữ liệu",
    );
  }

  return response.data;
};

export const getDataLockOptions = async (): Promise<DataLockOptions> => {
  const response = await API.get<DataLockApiEnvelope<DataLockOptions>>(
    "/admin/data-locks/options",
  ).then((res) => res.data);

  if (response?.success !== true || !isDataLockOptions(response.data)) {
    throw new Error(response?.message || "Không thể tải tùy chọn khóa dữ liệu");
  }

  return response.data;
};

export const getDataLockOrderFieldOptions = async (
  fieldCode: string,
  params: DataLockFieldOptionParams,
): Promise<DataLockPaginatedEnvelope<DataLockFieldOption>> => {
  const response = await API.get<DataLockFieldOptionsApiEnvelope>(
    `/admin/data-locks/options/order-fields/${encodeURIComponent(fieldCode)}`,
    { params },
  ).then((res) => res.data);

  if (
    response?.success !== true ||
    !Array.isArray(response.data) ||
    hasInvalidPaginationMetadata(response)
  ) {
    throw new Error(response?.message || "Không thể tải tùy chọn trường");
  }

  const data = response.data.flatMap((option): DataLockFieldOption[] => {
    if (!isNonArrayObject(option)) return [];
    const value = [option.value, option._id, option.code]
      .map(toTrimmedString)
      .find(isString);
    const label = [option.label, option.name, option.code]
      .map(toTrimmedString)
      .find(isString);
    return value && label ? [{ value, label }] : [];
  });

  const totalItem = response.totalItem ?? response.totalDocs ?? 0;
  const pageSize = response.limit ??
    (isPositiveInteger(params.pageSize) ? params.pageSize : 1);
  const current = response.page ??
    (isPositiveInteger(params.current) ? params.current : 1);
  const totalPages = response.totalPages ??
    Math.max(1, Math.ceil(totalItem / pageSize));
  return { data, totalItem, current, pageSize, totalPages };
};

export const getDataLockCostConfig = async (): Promise<DataLockCostConfigNode[]> => {
  const response = await API.get<DataLockApiEnvelope<DataLockCostConfigNode[]>>(
    "/admin/orderCostConfig",
  ).then((res) => res.data);

  if (
    response?.success !== true ||
    !Array.isArray(response.data) ||
    !response.data.every(isCostConfigNode)
  ) {
    throw new Error(response?.message || "Không thể tải cấu hình chi phí đơn hàng");
  }

  return response.data;
};

export const createDataLock = async (
  payload: DataLockMutationPayload,
): Promise<void> =>
  ensureSuccessfulMutation(API.post("/admin/data-locks", payload));

export const updateDataLock = async (
  id: string,
  payload: DataLockMutationPayload,
): Promise<void> =>
  ensureSuccessfulMutation(API.put(`/admin/data-locks/${id}`, payload));

export const deleteDataLock = async (id: string): Promise<void> =>
  ensureSuccessfulMutation(API.delete(`/admin/data-locks/${id}`));

export default {
  getAllDataLocks,
  getDataLock,
  getDataLockOptions,
  getDataLockOrderFieldOptions,
  getDataLockCostConfig,
  createDataLock,
  updateDataLock,
  deleteDataLock,
};
